// What's lying around the map, and where.
//
// The world is infinite and generated from a seed, so pickup SITES are too: they're
// rolled per chunk out of the same deterministic stream as the platforms they sit
// on. Nothing is stored, nothing is shipped, and a site a mile away exists in the
// same place for everyone the moment someone walks to it. Only the *state* of a
// site — taken, or back — is the server's to know, because that's the part players
// disagree about.
//
// The split of labour: engine/chunks.js decides WHERE (it has the terrain and the
// rng, and no idea what a Stonks is), and this file decides WHAT (it has the item
// table, and no idea how the world is generated). A structure's loot is the one
// crossover — chunks.js emits it as item-NAME strings, which mean nothing to it and
// everything here.
//
// Tune it in data/pickups.json:
//   frequency  relative weight — how often this item is the one a scattered site
//              gets. A weight, not a probability, so you can add items without
//              rebalancing every other number. Health and armour sit low on purpose.
//   respawn    seconds before it comes back after someone takes it.
//   sites      chancePerSurface / maxPerChunk / hover — how thick the scatter is.

import { chunkRng, chunkX, chunkY } from "../engine/chunks.js";
import PICKUPS from "../../data/pickups.json" with { type: "json" };

const SALT = 0x5bf03635; // this decorator's own rng stream (see chunkRng)
const SITES = PICKUPS.sites;

export const ITEMS = PICKUPS.items;
export const itemNames = () => Object.keys(ITEMS);
export const item = (name) => ITEMS[name];

// The weighted table, built once. A site carries a roll in [0,1);
// this turns it into an item name. Doing it here rather than in the
// chunk generator is what lets the item list change without moving a
// single platform or site.
const TABLE = (() => {
    const rows = Object.entries(ITEMS);
    const total = rows.reduce((n, [, it]) => n + (it.frequency || 0), 0);
    let acc = 0;
    return {
        total,
        cuts: rows.map(([name, it]) => ({ name, upto: (acc += (it.frequency || 0)) / total })),
    };
})();

export function itemForRoll(roll) {
    for (const c of TABLE.cuts) if (roll < c.upto) return c.name;
    return TABLE.cuts[TABLE.cuts.length - 1].name;
}

// A per-sim item catalog: the base scatter items PLUS whatever the enabled plugins ship (their
// items.json specs, gathered in installPlugins). It owns the def lookup + the weighted roll table, so
// a plugin-registered item (a ration, a seed) scatters and is handed out exactly like a base one.
// `extra` is an array of { name, spec }. A bare sim has no catalog and falls back to itemForRoll /
// item() above — this only layers on top when plugins are loaded. Roll is the same cumulative-weight
// scheme as TABLE, so a stored site roll in [0,1) maps deterministically to a name for the whole room.
export function createItemCatalog(extra = []) {
    const items = { ...ITEMS };
    for (const { name, spec } of extra) items[name] = spec;
    const rows = Object.entries(items);
    const total = rows.reduce((n, [, it]) => n + (it.frequency || 0), 0);
    let acc = 0;
    const cuts = rows.map(([name, it]) => ({ name, upto: total ? (acc += (it.frequency || 0)) / total : 1 }));
    return {
        def: (name) => items[name],
        roll: (r) => { for (const c of cuts) if (r < c.upto) return c.name; return cuts[cuts.length - 1].name; },
        names: () => Object.keys(items),
    };
}

const cache = new Map();

// Sites for one chunk: guaranteed structure loot, plus a scatter of
// items on the terrain's exposed tops. Cached like the chunk grids
// are — a pure function of (seed, chunk).
export function sitesInChunk(gen, cx, cy) {
    // Keyed by the generator (name + seed), so two rooms on the same seed but different
    // generators don't share each other's scatter.
    const key = `${gen.name}:${gen.seed}:${cx}:${cy}`;
    const hit = cache.get(key);
    if (hit) return hit;

    // Both kinds sit `hover` above the surface they belong to (the
    // chunk gives that y as a tile top). Structure loot first — it's
    // forced, so it always makes the cut before the cap.
    // Structure loot carries an explicit item NAME; a scatter site carries only its raw ROLL in
    // [0,1). The name for a roll is resolved per-sim (a room's enabled plugins change the item set),
    // so the site geometry here stays a pure, sim-independent, cached function of (generator, chunk).
    const lift = (s) => ({ id: s.id, x: s.x, y: s.y - SITES.hover, item: s.item, roll: s.roll });
    const sites = gen.structureSitesInChunk(cx, cy).map(lift);

    // Then scatter items across the surfaces. Both rolls are drawn
    // for EVERY surface, placed or not, so whether one surface gets a
    // site doesn't shift which item the next one gets — consuming the
    // stream conditionally is how seeded generators quietly become
    // order-dependent.
    const rng = chunkRng(gen.seed, cx, cy, SALT);
    for (const surf of gen.surfacesInChunk(cx, cy)) {
        const place = rng();
        const roll = rng();
        if (place > SITES.chancePerSurface) continue;
        if (sites.length >= SITES.maxPerChunk) continue;
        sites.push(lift({ id: surf.id, x: surf.x, y: surf.y, roll }));
    }

    cache.set(key, sites);
    return sites;
}

// Every site in the chunks around a point — the ones a player
// standing there could plausibly reach. Same 3×3 neighbourhood the
// collision uses.
export function sitesNear(gen, x, y) {
    const out = [];
    const cx0 = chunkX(x), cy0 = chunkY(y);
    for (let cx = cx0 - 1; cx <= cx0 + 1; cx++) {
        for (let cy = cy0 - 1; cy <= cy0 + 1; cy++) {
            for (const s of sitesInChunk(gen, cx, cy)) out.push(s);
        }
    }
    return out;
}
